// src/pages/FounderDashboard.tsx - Complete with working Supabase sync
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Rocket, 
  FileText, 
  Users, 
  Bell, 
  PlusCircle,
  Loader2,
  RefreshCw,
  Wifi,
  WifiOff,
  Trash2,
  RotateCcw
} from 'lucide-react';
import { demoPitchData } from '@/data/demoPitchData';

export default function FounderDashboard() {
  const { user } = useAuth();
  const { pitches, createPitch, restorePitch, permanentlyDeletePitch, globalRefreshKey, refreshPitches, isSupabaseConnected } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Restore dialog state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deletedDemoPitch, setDeletedDemoPitch] = useState<any>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  
  // Permanent delete confirmation
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [pitchToPermanentlyDelete, setPitchToPermanentlyDelete] = useState<string | null>(null);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshPitches();
    setLastUpdated(new Date());
    setIsRefreshing(false);
    toast({ title: "Refreshed", description: "Data updated." });
  };

  // Get user's pitches with refresh key to force re-render
  const userPitches = pitches.filter(p => p.founderId === user?.id);
  const activePitches = userPitches.filter(p => !p.deletedAt && p.stage !== 'funding');
  const deletedPitches = userPitches.filter(p => p.deletedAt === true);

  const stats = {
    totalPitches: userPitches.length,
    activePitches: activePitches.length,
    deletedPitches: deletedPitches.length,
    notifications: 0,
    teamMembers: 1
  };

  const handleCreateDemoPitch = async () => {
    if (!user) return;
    
    console.log('Creating demo pitch...');
    setIsProcessing(true);
    
    try {
      // Check if demo pitch already exists AND is NOT deleted
      const existingDemo = userPitches.find(p => 
        p.companyName === 'EcoTrack Solutions' && !p.deletedAt
      );
      
      if (existingDemo) {
        toast({
          title: "Demo Pitch Already Exists",
          description: "You already have a demo pitch in your dashboard",
        });
        navigate(`/pitch/${existingDemo.id}`);
        return;
      }
      
      // Check for deleted demo pitch that can be restored
      const deletedDemo = userPitches.find(p => 
        p.companyName === 'EcoTrack Solutions' && p.deletedAt === true
      );
      
      if (deletedDemo) {
        setDeletedDemoPitch(deletedDemo);
        setRestoreDialogOpen(true);
        return;
      }
      
      // Create new demo pitch
      const newPitchId = await createPitch({
        ...demoPitchData,
        founderId: user.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        stage: 'idea',
        status: 'draft'
      });
      
      console.log('Demo pitch created with ID:', newPitchId);
      
      toast({
        title: "Demo Pitch Loaded",
        description: "Sample pitch 'EcoTrack Solutions' has been added to your dashboard",
      });
      
      navigate(`/pitch/${newPitchId}`);
    } catch (error) {
      console.error('Failed to create demo pitch:', error);
      toast({
        title: "Error",
        description: "Failed to create demo pitch. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestoreDemoPitch = async () => {
    if (!deletedDemoPitch || !user) return;
    
    setIsRestoring(true);
    try {
      const success = await restorePitch(deletedDemoPitch.id, user.id);
      
      if (success) {
        toast({
          title: "Demo Pitch Restored",
          description: "Your demo pitch has been restored to your dashboard.",
        });
        setRestoreDialogOpen(false);
        setDeletedDemoPitch(null);
        navigate(`/pitch/${deletedDemoPitch.id}`);
      } else {
        throw new Error('Restore failed');
      }
    } catch (error) {
      console.error('Restore failed:', error);
      toast({
        title: "Restore Failed",
        description: "Could not restore the demo pitch. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleRestorePitch = async (pitchId: string) => {
    if (!pitchId || !user?.id) return;
    
    setIsProcessing(true);
    try {
      const success = await restorePitch(pitchId, user.id);
      
      if (success) {
        toast({
          title: "Pitch Restored",
          description: "The pitch has been restored to your dashboard.",
        });
      } else {
        throw new Error('Restore failed');
      }
    } catch (error) {
      console.error('Restore failed:', error);
      toast({
        title: "Restore Failed",
        description: "Could not restore the pitch. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!pitchToPermanentlyDelete || !user?.id) return;
    
    setIsProcessing(true);
    try {
      const success = await permanentlyDeletePitch(pitchToPermanentlyDelete, user.id);
      
      if (success) {
        toast({
          title: "Pitch Permanently Deleted",
          description: "The pitch has been permanently removed from the system.",
        });
        setPermanentDeleteDialogOpen(false);
        setPitchToPermanentlyDelete(null);
      } else {
        throw new Error('Deletion failed');
      }
    } catch (error) {
      console.error('Permanent delete failed:', error);
      toast({
        title: "Deletion Failed",
        description: "Could not permanently delete the pitch. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNavigate = (path: string, buttonName: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`🔍 [${buttonName}] Navigation attempted to: ${path}`);
    
    try {
      navigate(path);
    } catch (error) {
      console.error(`❌ Navigation error to ${path}:`, error);
      toast({
        title: "Navigation Error",
        description: `Could not navigate to ${path}.`,
        variant: "destructive"
      });
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" key={globalRefreshKey}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Welcome back, {user.name}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your ventures today.
          </p>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-xs text-muted-foreground">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
            <div className="flex items-center gap-1">
              {isSupabaseConnected ? (
                <>
                  <Wifi className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600">Reconnecting...</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          
          {user.email === 'demo@founder.com' && (
            <Button 
              variant="outline" 
              className="border-blue-500 text-blue-600 hover:bg-blue-50" 
              onClick={handleCreateDemoPitch}
              disabled={isProcessing}
            >
              <Rocket className="h-4 w-4 mr-2" />
              Load Sample Pitch
            </Button>
          )}
          
          <Button 
            onClick={handleNavigate('/pitch/new/basics', 'New Pitch Header')}
            className="bg-primary hover:bg-primary/90"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            New Pitch
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard 
          title="Total Pitches" 
          value={stats.totalPitches.toString()} 
          description="Across all stages" 
          icon={FileText} 
          color="bg-blue-500"
          onClick={handleNavigate('/pitches', 'Total Pitches Card')}
        />
        <StatCard 
          title="Active Pitches" 
          value={stats.activePitches.toString()} 
          description="In progress" 
          icon={Rocket} 
          color="bg-green-500"
          onClick={handleNavigate('/pitches?stage=active', 'Active Pitches Card')}
        />
        <StatCard 
          title="Notifications" 
          value="0" 
          description="Unread messages" 
          icon={Bell} 
          color="bg-yellow-500"
          onClick={handleNavigate('/notifications', 'Notifications Card')}
        />
        <StatCard 
          title="Team Members" 
          value="1" 
          description="Active users" 
          icon={Users} 
          color="bg-purple-500"
          onClick={handleNavigate('/founder-profile', 'Team Members Card')}
        />
      </div>

      {/* Deleted Pitches Section (if any) */}
      {deletedPitches.length > 0 && (
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Deleted Pitches
            </CardTitle>
            <CardDescription className="text-red-600">
              You have {deletedPitches.length} deleted pitch{deletedPitches.length !== 1 ? 'es' : ''}. 
              Deleted pitches can be restored or permanently removed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {deletedPitches.map(pitch => (
                <div key={pitch.id} className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-red-100">
                  <div>
                    <p className="font-medium text-sm">{pitch.companyName}</p>
                    <p className="text-xs text-muted-foreground">
                      Deleted on {pitch.deletedAt ? new Date(pitch.deletedAt).toLocaleDateString() : 'Unknown date'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => handleRestorePitch(pitch.id)}
                      disabled={isProcessing}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Restore
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-red-500 text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setPitchToPermanentlyDelete(pitch.id);
                        setPermanentDeleteDialogOpen(true);
                      }}
                      disabled={isProcessing}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete Forever
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Your latest pitch activities</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {activePitches.length > 0 ? (
            <div className="space-y-4">
              {activePitches
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                .slice(0, 5)
                .map(pitch => (
                  <div 
                    key={pitch.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={handleNavigate(`/pitch/${pitch.id}`, `Pitch: ${pitch.companyName}`)}
                  >
                    <div>
                      <p className="font-medium">{pitch.companyName || 'Untitled Pitch'}</p>
                      <p className="text-sm text-muted-foreground">{pitch.tagline}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Updated {new Date(pitch.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    {pitch.analysisScores && (
                      <div className={`text-sm font-medium px-3 py-1 rounded-full ${
                        pitch.analysisScores.overall >= 70 ? 'bg-green-100 text-green-700' :
                        pitch.analysisScores.overall >= 50 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        Score: {pitch.analysisScores.overall}/100
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>No recent activity</p>
              <p className="text-sm">Create a new pitch or click "Load Sample Pitch" to get started</p>
              <div className="flex gap-3 justify-center mt-4">
                <Button 
                  variant="default" 
                  onClick={handleNavigate('/pitch/new/basics', 'Create First Pitch')}
                >
                  Create your first pitch →
                </Button>
                {user.email === 'demo@founder.com' && (
                  <Button 
                    variant="outline" 
                    className="border-blue-500 text-blue-600"
                    onClick={handleCreateDemoPitch}
                    disabled={isProcessing}
                  >
                    <Rocket className="h-4 w-4 mr-2" />
                    Load Sample Pitch
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="default" 
              className="h-24 flex-col gap-2 hover:shadow-md transition-all" 
              onClick={handleNavigate('/pitch/new/basics', 'Quick Actions - New Pitch')}
            >
              <PlusCircle className="h-6 w-6" />
              <span>New Pitch</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:shadow-md transition-all" 
              onClick={handleNavigate('/pitches', 'Quick Actions - My Pitches')}
            >
              <FileText className="h-6 w-6" />
              <span>My Pitches</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:shadow-md transition-all" 
              onClick={handleNavigate('/notifications', 'Quick Actions - Notifications')}
            >
              <Bell className="h-6 w-6" />
              <span>Notifications</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:shadow-md transition-all" 
              onClick={handleNavigate('/founder-profile', 'Quick Actions - Profile')}
            >
              <Users className="h-6 w-6" />
              <span>Profile</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Restore Demo Pitch Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore Demo Pitch?</DialogTitle>
            <DialogDescription>
              You have a deleted demo pitch. Would you like to restore it instead of creating a new one?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Restoring will bring back "{deletedDemoPitch?.companyName}" with all its data.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={async () => {
              setRestoreDialogOpen(false);
              setDeletedDemoPitch(null);
              setIsProcessing(true);
              try {
                const newPitchId = await createPitch({
                  ...demoPitchData,
                  founderId: user.id,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  stage: 'idea',
                  status: 'draft'
                });
                navigate(`/pitch/${newPitchId}`);
              } catch (error) {
                toast({
                  title: "Error",
                  description: "Failed to create new pitch.",
                  variant: "destructive",
                });
              } finally {
                setIsProcessing(false);
              }
            }}>
              Create New Instead
            </Button>
            <Button onClick={handleRestoreDemoPitch} disabled={isRestoring}>
              {isRestoring ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Restore Demo Pitch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <AlertDialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete This Pitch?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The pitch will be permanently deleted from the system
              and cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePermanentDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                <>Delete Forever</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// StatCard with onClick handler
function StatCard({ title, value, description, icon: Icon, color, onClick }: any) {
  return (
    <Card 
      className={`${onClick ? 'cursor-pointer hover:shadow-md transition-all' : ''}`}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </div>
          <div className={`${color} p-3 rounded-full text-white`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}